import { layoutTextLayer } from './textLayout';

// Where a photo sits in the box it fills, and how it moves inside it: an image layer, a
// shape filled with a photo and a text filled with a photo all frame it the same way
// (`fit` cover/contain × `zoom`, placed by `focus_x`/`focus_y`). The renderer paints with
// `placement`; the stage's move mode (the photo dragged inside its mask) uses `panFocus`
// and `zoomFocus`, so the two always agree.

// The framing a layer paints with: the image layer itself, or a photo paint (shape, text).
export function framingOf(layer) {
  if (!layer) return null;
  if (layer.type === 'image' || layer.type === 'video') return layer;
  if (layer.type === 'shape' && layer.fill?.type === 'image') return layer.fill;
  if (layer.type === 'text' && layer.style?.fill?.type === 'image') return layer.style.fill;
  return null;
}

// The patch that changes a layer's framing (image layers keep it at the top level).
export function framingPatch(layer, patch) {
  if (layer.type === 'image' || layer.type === 'video') return patch;
  if (layer.type === 'shape') return { fill: { ...layer.fill, ...patch } };
  return { style: { fill: { ...layer.style.fill, ...patch } } };
}

// The box the photo is framed in: the layer's, a text's being as tall as its lines.
export const framingBox = layer => ({ width: layer.width, height: layer.type === 'text' ? layoutTextLayer(layer).height : layer.height });

export function placement(width, height, naturalWidth, naturalHeight, { fit = 'cover', zoom = 1, focus_x: focusX = 0.5, focus_y: focusY = 0.5 } = {}) {
  const s = (fit === 'contain' ? Math.min(width / naturalWidth, height / naturalHeight) : Math.max(width / naturalWidth, height / naturalHeight)) * zoom;
  const dw = naturalWidth * s;
  const dh = naturalHeight * s;
  return { dx: (width - dw) * focusX, dy: (height - dh) * focusY, dw, dh };
}

const clamp01 = value => Math.min(1, Math.max(0, value));
const round3 = value => Math.round(value * 1000) / 1000;

// The focus that moves the photo by (mx, my) box pixels. The photo never slides past its
// box: covering photos keep covering it, contained ones stay inside it.
export function panFocus(layer, mx, my) {
  const framing = framingOf(layer);
  if (!framing?.source) return null;
  const { width, height } = framingBox(layer);
  const { dx, dy, dw, dh } = placement(width, height, framing.source.width, framing.source.height, framing);
  const focus = (start, move, room, current) => (Math.abs(room) < 0.5 ? current : round3(clamp01((start + move) / room)));
  return { focus_x: focus(dx, mx, width - dw, framing.focus_x), focus_y: focus(dy, my, height - dh, framing.focus_y) };
}

// A pointer drag on the canvas, in document pixels, as a move of the photo in its box:
// undoes the layer's rotation and, for image layers, its flips.
export function boxDelta(layer, x, y) {
  const angle = (-(layer.rotation || 0) * Math.PI) / 180;
  let mx = x * Math.cos(angle) - y * Math.sin(angle);
  let my = x * Math.sin(angle) + y * Math.cos(angle);
  const flips = layer.type === 'image' || layer.type === 'video';
  if (flips && layer.flip_x) mx = -mx;
  if (flips && layer.flip_y) my = -my;
  return { mx, my };
}

// Zooming the photo inside its box keeps the box's center on the same part of the photo.
export function zoomFocus(layer, zoom) {
  const framing = framingOf(layer);
  if (!framing?.source) return null;
  const next = Math.round(Math.min(5, Math.max(1, zoom)) * 100) / 100;
  const { width, height } = framingBox(layer);
  const before = placement(width, height, framing.source.width, framing.source.height, framing);
  const after = placement(width, height, framing.source.width, framing.source.height, { ...framing, zoom: next });
  // The photo point under the box center, kept there.
  const u = (width / 2 - before.dx) / before.dw;
  const v = (height / 2 - before.dy) / before.dh;
  const dx = width / 2 - u * after.dw;
  const dy = height / 2 - v * after.dh;
  const focus = (value, room, current) => (Math.abs(room) < 0.5 ? current : round3(clamp01(value / room)));
  return { zoom: next, focus_x: focus(dx, width - after.dw, framing.focus_x), focus_y: focus(dy, height - after.dh, framing.focus_y) };
}
